import { createContext, Dispatch, SetStateAction } from 'react';
import type { NotionalLimitConfigurationErrors } from 'state';

/** Live, not-yet-saved instrument selection shared between the instrument table
 *  and the surrounding shell (e.g. the Dense rail's "N Instruments" subtext) so
 *  the count updates as rows are checked, before Save persists to redux.
 *
 *  Optional by design: the selector reads this context when a provider is
 *  present (Dense shell) and otherwise falls back to its own internal state, so
 *  the legacy "Session Settings" tab — which renders the selector without a
 *  provider — keeps working unchanged. */
export interface InstrumentSelection {
  /** Currently selected underlying-asset ids (live, may be unsaved). */
  selected: number[];
  /** Replace the selection (checkbox toggle, preset apply, …). Typed as the
   *  React state setter so it's interchangeable with the selector's own
   *  useState setter in the controlled/uncontrolled fallback. */
  setSelected: Dispatch<SetStateAction<number[]>>;
  /** Draft limit policy saved atomically with the instrument universe. */
  limitMode?: 0 | 1;
  setLimitMode?: Dispatch<SetStateAction<0 | 1>>;
  /** Reporting-currency decimal strings keyed by underlying-asset id. */
  notionalLimits?: Record<number, string>;
  setNotionalLimits?: Dispatch<SetStateAction<Record<number, string>>>;
  /** Dense trainer settings persist each pre-launch edit as a backend draft. */
  autoSaveDraft?: boolean;
  draftReady?: boolean;
  draftDirty?: boolean;
  draftSaving?: boolean;
  draftSaveStatus?: 'loading' | 'saved' | 'unsaved' | 'saving' | 'error';
  draftError?: string | null;
  draftLaunchReady?: boolean;
  draftConfigurationErrors?: NotionalLimitConfigurationErrors;
  flushDraft?: () => void;
  reloadDraft?: () => void;
}

export const InstrumentSelectionContext =
  createContext<InstrumentSelection | null>(null);
